import type { EnrichedMapAccount } from "./portfolioMapVisualHelpers";
import { ahPostureLabel } from "./portfolioMapVisualHelpers";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";

const BH_CLASS_LABEL: Record<string, string> = {
  strong: "Strong",
  stable: "Stable",
  concern: "Concern",
  no_data: "No data",
};

const RL_CONDITIONS_LABEL: Record<string, string> = {
  open: "open",
  cautious: "cautious",
  blocked: "blocked",
};

interface Props {
  account: EnrichedMapAccount;
  overview: PortfolioAccountRow | null;
}

function SummaryCell({
  label,
  value,
  title,
}: {
  label: string;
  value: string;
  title?: string;
}) {
  return (
    <div className="rounded-md bg-gray-50 px-2.5 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </div>
      <div
        className="mt-0.5 text-[12.5px] font-bold text-gray-900"
        title={title}
      >
        {value}
      </div>
    </div>
  );
}

/** Right-panel body when Signal service / Open Actions is off and a single
 * account is selected — source-data metrics only, no signal cards or CTAs. */
export default function PortfolioMapAccountSummary({
  account,
  overview,
}: Props) {
  const { accountHealth: ahThresholds } = useHealthScoreThresholds();
  const fit = account.fit != null ? String(Math.round(account.fit)) : "—";
  const bh =
    account.bh != null
      ? `${Math.round(account.bh)} · ${
          BH_CLASS_LABEL[account.bh_class] ?? account.bh_class
        }`
      : "—";
  const ah =
    account.ah != null
      ? `${Math.round(account.ah)} · ${ahPostureLabel(
          account.ah,
          ahThresholds,
          account.ah_cluster
        )}`
      : "Unscored · Unscored";
  const alLabel = account.al != null ? `AL ${account.al}` : "AL ?";
  const rl =
    overview?.my_rl != null
      ? `RL ${Math.round(overview.my_rl)}`
      : overview?.rl_aggregate != null
        ? `RL ${Math.round(overview.rl_aggregate)}`
        : "RL ?";
  const net =
    account.net_investment_signal != null
      ? String(Math.round(account.net_investment_signal))
      : "—";
  const renewal =
    overview?.renewal_days != null ? `${overview.renewal_days} days` : "—";
  const target =
    account.target_al != null
      ? `AL ${account.target_al} · not committed`
      : "— · not committed";
  const rlConditions =
    account.rl_conditions != null
      ? (RL_CONDITIONS_LABEL[account.rl_conditions] ?? account.rl_conditions)
      : "—";

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-3">
      <p className="text-[13px] font-bold text-gray-900">Account summary</p>
      <p className="mt-1 text-[11.5px] leading-snug text-gray-500">
        Source-data context only. Signal prioritization and actions are not
        available in the current organization configuration.
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <SummaryCell label="Strategic relevance" value={fit} />
        <SummaryCell label="Business Health" value={bh} />
        <SummaryCell label="Account Health" value={ah} />
        <SummaryCell
          label="Alignment / relationships"
          value={`${alLabel} · ${rl}`}
          title={alLabel === "AL ?" || rl === "RL ?" ? "Unassessed" : undefined}
        />
        <SummaryCell label="Net investment context" value={net} />
        <SummaryCell label="Renewal" value={renewal} />
        <SummaryCell label="Target recommendation" value={target} />
        <SummaryCell label="RL conditions" value={rlConditions} />
      </div>
    </div>
  );
}
